import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation, useSearch } from "wouter";
import type { z } from "zod";
import { AuthLayout } from "../components/shell.tsx";
import { Alert, Button, Panel, PasswordField, TextField } from "../components/ui.tsx";
import { ApiError, applyFieldErrors, post } from "../lib/api.ts";
import { safeNext, useSession } from "../lib/session.tsx";
import { loginSchema, type SessionDto } from "../../shared/auth.ts";
import { usePageTitle } from "../lib/nav.tsx";

type Form = z.input<typeof loginSchema>;

export function LoginPage() {
  usePageTitle("Entrar");
  const { adopt } = useSession();
  const [, navigate] = useLocation();
  const next = safeNext(new URLSearchParams(useSearch()).get("next"));
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Form>({ resolver: zodResolver(loginSchema), defaultValues: { email: "", password: "" } });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const res = await post<{ data: SessionDto }>("/auth/login", values);
      adopt(res.data);
      navigate(next ?? "/planos", { replace: true });
    } catch (e) {
      applyFieldErrors(e, form.setError as never);
      setError(e instanceof ApiError ? e.message : "Não foi possível entrar agora. Tente novamente.");
    }
  });

  return (
    <AuthLayout>
      <Panel>
        <h1 className="text-xl font-bold">Acesse sua oficina</h1>
        <p className="mt-1 text-sm text-muted">Entre para consultar e construir o plano de gestão.</p>
        {error && <Alert tone="danger">{error}</Alert>}
        <form onSubmit={onSubmit} noValidate className="mt-2">
          <TextField label="E-mail" type="email" autoComplete="username" inputMode="email" autoFocus
            error={form.formState.errors.email?.message} {...form.register("email")} />
          <PasswordField label="Senha" autoComplete="current-password" error={form.formState.errors.password?.message} {...form.register("password")} />
          <Button type="submit" className="mt-3 w-full" loading={form.formState.isSubmitting}>Entrar</Button>
        </form>
        <p className="mt-4 text-sm">
          <Link href="/recuperar" className="font-semibold text-primary">Esqueci minha senha</Link>
        </p>
        <p className="mt-4 border-t border-line pt-4 text-xs text-muted">
          Não há cadastro público. O instrutor da oficina envia um convite para o seu e-mail.
        </p>
      </Panel>
    </AuthLayout>
  );
}
